import React, { createContext, useContext, useCallback, useState, useEffect, useRef } from "react";
import axios from "axios";
import { tableFromIPC, compressionRegistry, CompressionType } from "apache-arrow";
import Cookies from "js-cookie";
import * as fzstd from "fzstd";

// ZSTD decompression helper
function decompressZSTD(compressedData) {
    try {
        const decompressed = fzstd.decompress(new Uint8Array(compressedData));
        return new Uint8Array(decompressed);
    } catch (error) {
        throw new Error(`Failed to decompress ZSTD data: ${error.message}`);
    }
}

// Custom ZSTD codec for Arrow
// Note: encode is intentionally omitted since we only need decoding for reading compressed data
const ZSTDCodec = {
    decode: (data) => {
        return decompressZSTD(data);
    }
};

const QueryDashboardContext = createContext();

export const QueryDashboardProvider = ({ children }) => {
    const FIVE_MINUTES_MS = 300000;

    // Register ZSTD codec for Arrow IPC (do this once on module load)
    useEffect(() => {
        try {
            compressionRegistry.set(CompressionType.ZSTD, ZSTDCodec);
        } catch (e) {
            console.warn("ZSTD codec registration failed:", e.message);
        }
    }, []);

    // Session state
    const [connectionInfo, setConnectionInfo] = useState(null);
    const [jwtToken, setJwtToken] = useState(() => Cookies.get("jwtToken") || null);
    const jwtTokenRef = useRef(Cookies.get("jwtToken") || null);

    // Load connection info from cookies on mount
    useEffect(() => {
        const savedConnection = Cookies.get("connectionInfo");
        if (savedConnection) {
            try {
                setConnectionInfo(JSON.parse(savedConnection));
            } catch (e) {
                console.error("Failed to parse saved connection info:", e);
            }
        }
    }, []);

    // --- Normalization / Parsing ---
    const parseResponseData = (data) => {
        let tableData = [];
        try {
            if (data?.payloadBase64 || data?.binary || data?.base64) {
                const base64 = data.payloadBase64 || data.base64 || data.binary;
                const binary = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));

                const table = tableFromIPC(binary);
                const rows = Array.from(table.toArray());
                const columns = table.schema.fields.map((f) => f.name);

                tableData = rows.map((row) => {
                    const obj = {};
                    columns.forEach((col) => (obj[col] = row[col]));
                    return obj;
                });
            } else if (Array.isArray(data?.results)) {
                tableData = data.results;
            } else if (Array.isArray(data?.data)) {
                tableData = data.data;
            } else if (Array.isArray(data)) {
                tableData = data;
            } else if (typeof data === "object" && data !== null) {
                tableData = [data];
            }
        } catch (e) {
            throw new Error("Error decoding response: " + e.message);
        }
        return tableData;
    };

    const persistJwtToken = (token) => {
        jwtTokenRef.current = token;
        setJwtToken(token);

        const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
        Cookies.set("jwtToken", token, {
            path: "/",
            secure: !isLocalhost,
            sameSite: "lax"
        });
    };

    // --- Login ---
    const login = async (serverUrl, username, password, splitSize, claims) => {
        try {
            const response = await axios.post(`${serverUrl.trim()}/v1/login`, {
                username,
                password,
                claims: claims,
            });
            const jwt = `${response.data.tokenType} ${response.data.accessToken}`;

            // Update both ref and cookie synchronously so immediate follow-up requests
            // cannot keep using a stale in-memory token from the previous session.
            persistJwtToken(jwt);

            // Save connection info (without password for security in cookies)
            const connInfo = {
                serverUrl: serverUrl.trim(),
                username,
                claims,
                splitSize, // Default, will be updated when executing queries
                loginTime: new Date().toISOString()
            };

            Cookies.set("connectionInfo", JSON.stringify(connInfo), { path: "/" });

            setConnectionInfo(connInfo);

            return jwt;
        } catch (err) {
            if (err.response) {
                const code = err.response.status;
                const text = err.response.statusText || "Upstream server error";
                throw new Error(`Failed with response ${code} ${text}`);
            }
            throw err;
        }
    };

    // --- Login with JWT (direct token authentication) ---
    const loginWithJwt = async (serverUrl, jwt, splitSize, username = "jwt_user") => {
        try {
            const trimmedJwt = jwt.trim();

            // Validate JWT format (basic check)
            if (!trimmedJwt) {
                throw new Error("JWT token is required");
            }

            // Check if JWT already has "Bearer" prefix, if not add it
            const formattedJwt = trimmedJwt.startsWith("Bearer ") ? trimmedJwt : `Bearer ${trimmedJwt}`;

            // Optionally, validate the token by making a test query or using a validation endpoint
            // For now, we'll just store the token and connection info

            // Update both ref and cookie synchronously so immediate follow-up requests
            // cannot keep using a stale in-memory token from the previous session.
            persistJwtToken(formattedJwt);

            // Save connection info (minimal, since we only have URL and JWT)
            const connInfo = {
                serverUrl: serverUrl.trim(),
                username,
                claims: {},
                splitSize,
                loginTime: new Date().toISOString(),
                jwtMode: true // Flag to indicate this was a JWT login
            };

            Cookies.set("connectionInfo", JSON.stringify(connInfo), { path: "/" });

            setConnectionInfo(connInfo);

            return formattedJwt;
        } catch (err) {
            throw err;
        }
    };

    // --- Logout ---
    const logout = useCallback(() => {
        Cookies.remove("jwtToken", { path: "/" });
        Cookies.remove("connectionInfo", { path: "/" });
        jwtTokenRef.current = null;
        setJwtToken(null);
        setConnectionInfo(null);
    }, []);


    // --- Helper: Check jwt and logout ---
    const requireJwtOrLogout = (jwt) => {
        const cookieToken = Cookies.get("jwtToken") || null;
        const token = cookieToken || jwtTokenRef.current || jwtToken || jwt;
        if (!token) {
            logout();
            throw new Error("Session expired. Please login again.");
        }
        return token;
    };

    // --- Helper: detect JSON ---
    const looksLikeJson = (buf) => {
        try {
            const s = new TextDecoder("utf-8").decode(buf.slice(0, 256)).trim();
            return s.startsWith("{") || s.startsWith("[") || s.startsWith('"');
        } catch {
            return false;
        }
    };

    // --- Core forwarder ---
    const forwardToDazzleDuck = async (serverUrl, query, jwt, queryId = null) => {
        if (!/^https?:\/\//i.test(serverUrl.trim())) {
            throw new Error("Server URL must start with http:// or https://");
        }

        const token = requireJwtOrLogout(jwt);

        const headers = {
            "Content-Type": "application/json",
            Accept: "application/json, application/vnd.apache.arrow.stream",
            Authorization: token ? token : jwt,
        };

        // Prepare request body with queryId (as number)
        let requestBody;
        if (typeof query === "string") {
            requestBody = queryId !== null ? { query, id: queryId } : { query };
        } else {
            requestBody = queryId !== null ? { ...query, id: queryId } : query;
        }

        try {
            const response = await axios.post(
                serverUrl,
                requestBody,
                {
                    responseType: "arraybuffer",
                    headers,
                    timeout: FIVE_MINUTES_MS,
                    maxContentLength: 50 * 1024 * 1024,
                }
            );

            const contentType = (response.headers["content-type"] || "").toLowerCase();
            let buffer;

            if (response.data instanceof ArrayBuffer) {
                buffer = new Uint8Array(response.data);
            } else if (ArrayBuffer.isView(response.data)) {
                buffer = new Uint8Array(response.data.buffer);
            } else if (typeof response.data === "string") {
                buffer = new TextEncoder().encode(response.data);
            } else {
                throw new Error("Unexpected response data type");
            }

            const safeBuffer = buffer.slice(0);

            if (contentType.includes("application/json") || looksLikeJson(safeBuffer)) {
                const jsonText = new TextDecoder("utf-8").decode(safeBuffer);
                const parsed = JSON.parse(jsonText);
                return { type: "json", data: parsed };
            }

            const base64 = btoa(String.fromCharCode(...safeBuffer));
            return { type: "binary", contentType, base64 };
        } catch (err) {
            throw err;
        }
    };

    // --- Execute Query ---
    const executeQuery = useCallback(async (serverUrl, query, splitSize, jwt, queryId) => {
        const cleanUrl = serverUrl.trim();
        if (!serverUrl || !query) {
            throw new Error("Please fill in all fields before running the query.");
        }

        const numericSplitSize = Number(splitSize);
        let finalResults = [];

        try {
            if (numericSplitSize <= 0) {
                let url = cleanUrl.endsWith("/v1/query")
                    ? cleanUrl
                    : cleanUrl.replace(/\/+$/, "") + "/v1/query";

                const result = await forwardToDazzleDuck(url, query, jwt, queryId);
                finalResults = result.type === "json"
                    ? parseResponseData(result.data)
                    : parseResponseData({
                        binary: true,
                        contentType: result.contentType,
                        payloadBase64: result.base64,
                    });
            } else {
                let planUrl = serverUrl.endsWith("/v1/plan")
                    ? serverUrl
                    : serverUrl.replace(/\/+$/, "") + "/v1/plan";
                planUrl += `?split_size=${numericSplitSize}`;

                const planResult = await forwardToDazzleDuck(planUrl, { query }, jwt, queryId);
                const splits = Array.isArray(planResult.data) ? planResult.data : [];

                if (splits.length === 0) throw new Error("No splits returned from /v1/plan endpoint.");

                for (const split of splits) {
                    const sql = split.query || split.sql;
                    if (!sql) continue;

                    const splitUrl = serverUrl.endsWith("/v1/query")
                        ? serverUrl
                        : serverUrl.replace(/\/+$/, "") + "/v1/query";

                    const splitResult = await forwardToDazzleDuck(splitUrl, sql, jwt, queryId);
                    const normalized = splitResult.type === "json"
                        ? parseResponseData(splitResult.data)
                        : parseResponseData({
                            binary: true,
                            contentType: splitResult.contentType,
                            payloadBase64: splitResult.base64,
                        });

                    finalResults.push(...normalized);
                }
            }

            // Normalize again (for safety)
            const normalized = Array.isArray(finalResults)
                ? finalResults
                : parseResponseData(finalResults);

            // Update connection info with splitSize
            if (connectionInfo) {
                const updated = { ...connectionInfo, splitSize: numericSplitSize };
                setConnectionInfo(updated);
                Cookies.set("connectionInfo", JSON.stringify(updated), { path: "/" });
            }

            return { data: normalized, queryId };
        } catch (err) {
            // Handle axios errors with response data
            if (err.response && err.response.data) {
                // Decode the ArrayBuffer response to get the actual error message
                const responseData = err.response.data;

                if (responseData instanceof ArrayBuffer) {
                    const decoder = new TextDecoder('utf-8');
                    const errorText = decoder.decode(responseData);

                    // Create a new error with the server's error message
                    throw new Error(errorText);
                } else if (typeof responseData === 'string') {
                    throw new Error(responseData);
                } else if (responseData && typeof responseData === 'object') {
                    const errorMsg = responseData.message || responseData.error || JSON.stringify(responseData);
                    throw new Error(errorMsg);
                }
            }
            // For other errors (network, timeout), preserve original
            throw err;
        }
    }, [connectionInfo]);

    // --- Cancel Query ---
    const cancelQuery = useCallback(async (serverUrl, query, queryId) => {
        if (!serverUrl || !queryId) {
            throw new Error("Server URL and Query ID are required to cancel.");
        }

        const token = requireJwtOrLogout();

        const headers = {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: token,
        };

        try {
            let cancelUrl = serverUrl.endsWith("/v1/cancel")
                ? serverUrl
                : serverUrl.replace(/\/+$/, "") + "/v1/cancel";

            const response = await axios.post(
                cancelUrl,
                { query, id: queryId },
                {
                    headers,
                    timeout: 5000,
                }
            );

            // Accept status codes 200, 202, 409 as success (matching the test)
            if ([200, 202, 409].includes(response.status)) {
                return { success: true, status: response.status };
            }

            return { success: false, status: response.status };
        } catch (err) {
            const msg = err?.message || String(err);
            throw new Error(`Cancel request failed: ${msg}`);
        }
    }, []);

    // --- Named Query Support ---

    /**
     * Forward named query requests to DazzleDuck server
     * Reuses existing authentication, error handling, and timeout logic
     */
    const forwardNamedQueryRequest = async (serverUrl, method, endpoint, data = null) => {
        const cleanUrl = serverUrl.trim();
        if (!cleanUrl || !endpoint) {
            throw new Error("Server URL and endpoint are required.");
        }

        const token = requireJwtOrLogout();

        const headers = {
            "Content-Type": "application/json",
            Accept: "application/json, application/vnd.apache.arrow.stream",
            Authorization: token,
        };

        // Build full URL
        const fullUrl = cleanUrl.endsWith(endpoint)
            ? cleanUrl
            : cleanUrl.replace(/\/+$/, "") + endpoint;

        try {
            const config = {
                method,
                url: fullUrl,
                headers,
                timeout: FIVE_MINUTES_MS,
                maxContentLength: 50 * 1024 * 1024,
            };

            // Add request body for POST/PUT
            if (data && ['POST', 'PUT', 'PATCH'].includes(method.toUpperCase())) {
                config.data = data;
                // Set appropriate response type based on Accept header
                if (headers.Accept.includes('application/vnd.apache.arrow.stream')) {
                    config.responseType = 'arraybuffer';
                }
            }

            const response = await axios(config);

            const contentType = (response.headers["content-type"] || "").toLowerCase();

            // Handle arraybuffer responses (Arrow data)
            if (response.data instanceof ArrayBuffer) {
                const buffer = new Uint8Array(response.data);
                const base64 = btoa(String.fromCharCode(...buffer));
                return { type: "binary", contentType, base64 };
            }

            // Handle JSON responses
            return { type: "json", data: response.data };
        } catch (err) {
            // Handle axios errors with response data
            if (err.response && err.response.data) {
                const responseData = err.response.data;

                if (responseData instanceof ArrayBuffer) {
                    const decoder = new TextDecoder('utf-8');
                    const errorText = decoder.decode(responseData);
                    throw new Error(errorText);
                } else if (typeof responseData === 'string') {
                    throw new Error(responseData);
                } else if (responseData && typeof responseData === 'object') {
                    const errorMsg = responseData.message || responseData.error || JSON.stringify(responseData);
                    throw new Error(errorMsg);
                }
            }
            throw err;
        }
    };

    /**
     * Fetch all named queries with pagination
     * GET /v1/named-query?offset={offset}&limit={limit}
     * Note: Group filtering is now done client-side
     */
    const fetchNamedQueries = useCallback(async (serverUrl, offset = 0, limit = 20, group = null) => {
        if (!serverUrl?.trim()) {
            throw new Error("Server URL is required");
        }

        const cleanUrl = serverUrl.trim();
        const endpoint = `/v1/named-query`;
        const queryParams = `?offset=${offset}&limit=${Math.min(limit, 1000)}`;

        // Note: group parameter is kept for backward compatibility but no longer used
        // Groups are now derived from query_group field in the response

        try {
            const result = await forwardNamedQueryRequest(cleanUrl, 'GET', endpoint + queryParams);

            if (result.type === "json" && Array.isArray(result.data)) {
                // Transform parameterDescriptions from array format to object format for backward compatibility
                const transformedData = result.data.map(query => {
                    if (Array.isArray(query.parameterDescriptions)) {
                        const paramObj = {};
                        query.parameterDescriptions.forEach(param => {
                            paramObj[param.key] = param.value;
                        });
                        return { ...query, parameterDescriptions: paramObj };
                    }
                    return query;
                });
                return transformedData;
            }

            return [];
        } catch (err) {
            throw new Error(`Failed to fetch named queries: ${err.message}`);
        }
    }, []);

    /**
     * Get a specific named query by name
     * GET /v1/named-query/{name}
     */
    const getNamedQuery = useCallback(async (serverUrl, queryName) => {
        if (!serverUrl?.trim()) {
            throw new Error("Server URL is required");
        }

        if (!queryName?.trim()) {
            throw new Error("Query name is required");
        }

        const cleanUrl = serverUrl.trim();
        const endpoint = `/v1/named-query/${encodeURIComponent(queryName.trim())}`;

        try {
            const result = await forwardNamedQueryRequest(cleanUrl, 'GET', endpoint);

            if (result.type === "json") {
                // Transform parameterDescriptions from array format to object format
                let queryData = result.data;
                if (Array.isArray(queryData.parameterDescriptions)) {
                    const paramObj = {};
                    queryData.parameterDescriptions.forEach(param => {
                        paramObj[param.key] = param.value;
                    });
                    queryData = { ...queryData, parameterDescriptions: paramObj };
                }
                return queryData;
            }

            throw new Error("Invalid response format");
        } catch (err) {
            const errorMsg = err.message || "Failed to fetch named query";

            throw new Error(errorMsg);
        }
    }, []);

    /**
     * Execute a named query with parameters
     * POST /v1/named-query
     */
    const executeNamedQuery = useCallback(async (serverUrl, queryName, parameters = {}) => {
        if (!serverUrl?.trim()) {
            throw new Error("Server URL is required");
        }

        if (!queryName?.trim()) {
            throw new Error("Query name is required");
        }

        const cleanUrl = serverUrl.trim();
        const endpoint = `/v1/named-query`;

        // Prepare request body
        const requestBody = {
            name: queryName.trim(),
            parameters: parameters,
        };

        try {
            const token = requireJwtOrLogout();

            const headers = {
                "Content-Type": "application/json",
                "Authorization": token,
            };

            const fullUrl = cleanUrl.endsWith(endpoint)
                ? cleanUrl
                : cleanUrl.replace(/\/+$/, "") + endpoint;

            const config = {
                method: 'POST',
                url: fullUrl,
                headers,
                data: requestBody,
                timeout: FIVE_MINUTES_MS,
                maxContentLength: 50 * 1024 * 1024,
                responseType: 'arraybuffer',
            };

            const response = await axios(config);

            // Handle Arrow format (backend default)
            if (response.data instanceof ArrayBuffer) {
                const buffer = new Uint8Array(response.data);
                const base64 = btoa(String.fromCharCode(...buffer));
                return parseResponseData({ binary: true, base64 });
            }

            return [];
        } catch (err) {
            let errorMsg = err.message || "Failed to execute named query";

            // Handle specific HTTP errors
            if (err.response?.status === 404) {
                errorMsg = `Named query '${queryName}' not found`;
            } else if (err.response?.status === 400) {
                errorMsg = `Parameter validation failed for query '${queryName}'`;
            } else if (err.response?.status === 401) {
                errorMsg = "Unauthorized: Please login again";
            }

            // Handle axios errors with response data
            if (err.response && err.response.data) {
                const responseData = err.response.data;

                if (responseData instanceof ArrayBuffer) {
                    const decoder = new TextDecoder('utf-8');
                    errorMsg = decoder.decode(responseData);
                } else if (typeof responseData === 'string') {
                    errorMsg = responseData;
                } else if (responseData && typeof responseData === 'object') {
                    errorMsg = responseData.message || responseData.error || errorMsg;
                }
            }

            throw new Error(errorMsg);
        }
    }, []);

    // --- Save Session ---
    const saveSession = useCallback((currentQueries = []) => {
        if (!connectionInfo) {
            throw new Error("No active connection to save");
        }

        return {
            version: "1.0",
            savedAt: new Date().toISOString(),
            connection: {
                serverUrl: connectionInfo.serverUrl,
                username: connectionInfo.username,
                claims: connectionInfo.claims,
                splitSize: connectionInfo.splitSize || 0
            },
            queries: currentQueries.map(q => ({
                query: q.query,
                variables: q.variables || {},
                resultTitle: q.resultTitle || ""
            }))
        };
    }, [connectionInfo]);

    // --- Load Session ---
    const loadSession = useCallback((file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = (e) => {
                try {
                    const sessionData = JSON.parse(e.target.result);

                    // Validate session data structure
                    if (!sessionData.connection || !sessionData.queries) {
                        throw new Error("Invalid session file format");
                    }

                    resolve(sessionData);
                } catch (err) {
                    reject(new Error("Failed to parse session file: " + err.message));
                }
            };

            reader.onerror = () => {
                reject(new Error("Failed to read session file"));
            };

            reader.readAsText(file);
        });
    }, []);

    // --- Load Session from URL ---
    const loadSessionFromUrl = useCallback(async (url) => {
        try {
            const trimmedUrl = url.trim();

            // Validate URL protocol - only allow HTTP/HTTPS
            if (!trimmedUrl.startsWith("http://") && !trimmedUrl.startsWith("https://")) {
                throw new Error("Only HTTP and HTTPS URLs are supported");
            }

            const response = await axios.get(trimmedUrl);
            const sessionData = response.data;

            // Validate session data structure
            if (!sessionData.connection || !sessionData.queries) {
                throw new Error("Invalid session file format");
            }

            return sessionData;
        } catch (err) {
            if (err.response) {
                throw new Error(`Failed to fetch session: ${err.response.status} ${err.response.statusText}`);
            }
            throw new Error("Failed to load session from URL: " + err.message);
        }
    }, []);

    // --- Restore Session ---
    const restoreSession = useCallback(async (sessionData) => {
        const { connection, queries } = sessionData;

        // Just restore UI state, NOT authentication
        const connInfo = {
            serverUrl: connection.serverUrl,
            username: connection.username,
            claims: connection.claims,
            splitSize: connection.splitSize,
            loginTime: new Date().toISOString()
        };

        setConnectionInfo(connInfo);
        Cookies.set("connectionInfo", JSON.stringify(connInfo), { path: "/" });

        return {
            connection: connInfo,
            queriesRestored: queries?.length || 0
        };
    }, []);

    return (
        <QueryDashboardContext.Provider
            value={{
                executeQuery,
                login,
                loginWithJwt,
                logout,
                cancelQuery,
                saveSession,
                loadSession,
                loadSessionFromUrl,
                restoreSession,
                fetchNamedQueries,
                getNamedQuery,
                executeNamedQuery,
                connectionInfo,
            }}>
            {children}
        </QueryDashboardContext.Provider>
    );
};

export const useQueryDashboard = () => useContext(QueryDashboardContext);
